// deepLinks.js
// Addressable in-app ACTIONS, for links that must land the user inside a task
// rather than on the page that hosts it.
//
// Why this exists: several SkillsBridge actions (creating a JD, uploading an
// external JD) are drawers or modals opened from a button, so they have no route
// of their own. A link to the parent route drops the user on a list page and
// leaves them hunting for the control. Ask Allie's knowledge-base "Click here"
// links are the main consumer — they should open the thing the answer just
// described.
//
// Contract for a page that supports an action:
//   1. Read the param on mount (readDeepLinkAction).
//   2. Check the SAME entitlement that gates the equivalent button. A deep link
//      must never be a way around a permission check.
//   3. Strip the param (replace: true) so refresh and Back don't re-fire it.
//
// Authoring a route in the knowledge base: use buildDeepLink() to keep the
// query shape in one place, and keep the emitted value byte-identical to what
// the page parses.

/** Query-string key carrying the action. */
export const DEEP_LINK_PARAM = "action";

/** Known actions. Values are part of the knowledge base's Route data — changing
 *  one is a breaking change that requires re-embedding the KB.
 *
 *  Each entry needs a consumer page calling useDeepLinkAction; an action with no
 *  consumer silently does nothing, which is worse than having no link at all. */
export const DEEP_LINK_ACTIONS = {
  // /jobs-library — opens the JD creation drawer
  CREATE_JD: "create-jd",
  // /admin-setup/upload-external-jd — selects a tab (all three are on one route)
  UPLOAD_JD_SINGLE: "upload-jd-single",
  UPLOAD_JD_BULK: "upload-jd-bulk",
  // /admin-setup/job-codes — opens the Add Job Code drawer
  CREATE_JOB_CODE: "create-job-code",
  // /Skills-bridger-employee — opens the Source Files (evidence upload) drawer
  UPLOAD_SOURCE_FILES: "upload-source-files",
};

/** `/jobs-library` + CREATE_JD → `/jobs-library?action=create-jd` */
export const buildDeepLink = (path, action) =>
  `${path}?${DEEP_LINK_PARAM}=${encodeURIComponent(action)}`;

/** Read the requested action out of a location.search string, or null. */
export const readDeepLinkAction = (search) =>
  new URLSearchParams(search || "").get(DEEP_LINK_PARAM);

/** location.search with the action param removed, as a `?...` string or "". */
export const stripDeepLinkAction = (search) => {
  const params = new URLSearchParams(search || "");
  params.delete(DEEP_LINK_PARAM);
  const rest = params.toString();
  return rest ? `?${rest}` : "";
};
